import { linearTiming } from "@remotion/transitions";
import {
  TransitionDirection,
  TransitionKind,
  TransitionSpeed,
} from "@repo/api/transition";
import { msToFrames } from "../../utils/timing.ts";
import {
  getDefaultPreviewClipDurationsInFrames,
  ImageTransitionBuilder,
  ImageTransitionBuilderProps,
} from "./ImageTransitionBuilder.ts";
import { whipPan, WhipPanProps } from "../remocn/whip-pan.tsx";

const transitionKind = TransitionKind.enum.WHIP_PAN;

// NORMAL matches remocn's recommended 26 frames @ 30fps.
const WHIP_PAN_DURATION_MS: Record<TransitionSpeed, number> = {
  SLOW: 1200,
  NORMAL: 870,
  FAST: 600,
};

// Our direction is where the incoming scene enters from; remocn's is the way
// the camera whips, which is the opposite side.
const whipDirections: Partial<
  Record<TransitionDirection, NonNullable<WhipPanProps["direction"]>>
> = {
  FROM_RIGHT: "left",
  FROM_LEFT: "right",
  FROM_BOTTOM: "up",
  FROM_TOP: "down",
};

function getWhipPanDurationInFrames(props: ImageTransitionBuilderProps) {
  return msToFrames(
    WHIP_PAN_DURATION_MS[props.imageTransition.speed ?? "NORMAL"],
    props.fps,
  );
}

export const ImageWhipPanTransitionBuilder: ImageTransitionBuilder<WhipPanProps> =
  {
    formConfig: {
      kind: transitionKind,
      buildDefaultValue: () => ({
        kind: transitionKind,
        speed: "NORMAL",
        direction: "FROM_RIGHT",
      }),
      speed: ["SLOW", "NORMAL", "FAST"],
      direction: ["FROM_LEFT", "FROM_RIGHT", "FROM_TOP", "FROM_BOTTOM"],
    },

    transition: {
      getDurationInFrames: getWhipPanDurationInFrames,
      buildPresentation: (props) =>
        whipPan({
          direction:
            whipDirections[props.imageTransition.direction ?? "FROM_RIGHT"] ??
            "left",
        }),
      // The presentation eases internally, so the timing stays linear.
      buildTiming: (props) =>
        linearTiming({ durationInFrames: getWhipPanDurationInFrames(props) }),
    },

    getPreviewClipDurationsInFrames: getDefaultPreviewClipDurationsInFrames,
  };
